/**
 * Duty & inactivity check-ins (spec §14 SAFE-010/011).
 * Pure and deterministic: the server evaluates it; the field app mirrors it.
 */

export type DutyStatus = 'off' | 'available' | 'on_scene' | 'unavailable';

export interface CheckInSettings {
  /** Minutes without meaningful app activity before a check-in starts (default 30). */
  inactivityMinutes: number;
  /** Shorter limit while out of the vehicle at a door (default 15). */
  onSceneMinutes: number;
  /**
   * How long the deputy has to answer "Are you OK?" before dispatch is alerted (default 2).
   * Dispatch hears nothing until this runs out.
   */
  answerMinutes: number;
}

export const DEFAULT_CHECKIN: CheckInSettings = { inactivityMinutes: 30, onSceneMinutes: 15, answerMinutes: 2 };
export const MAX_ANSWER_MINUTES = 10;

export function checkInSettings(s: Record<string, unknown> | undefined): CheckInSettings {
  const n = (k: keyof CheckInSettings) => {
    const v = Number(s?.[k]);
    const max = k === 'answerMinutes' ? MAX_ANSWER_MINUTES : 480;
    return Number.isFinite(v) && v >= 1 && v <= max ? v : DEFAULT_CHECKIN[k];
  };
  // Older settings (graceMinutes / onSceneGraceMinutes) are ignored: the deputy gets answerMinutes either way.
  return { inactivityMinutes: n('inactivityMinutes'), onSceneMinutes: n('onSceneMinutes'), answerMinutes: n('answerMinutes') };
}

export type CheckInPhase = 'ok' | 'checkin_due' | 'overdue' | 'paused';

export interface CheckInState {
  phase: CheckInPhase;
  reason: 'inactive' | 'on_scene' | null;
  /** When the check-in starts / started. */
  dueAt: Date | null;
  /** When dispatch is / was alerted. */
  alertAt: Date | null;
  idleMinutes: number;
}

/**
 * Movement or GPS alone never resets the timer (SAFE-010); only meaningful app
 * activity does (SAFE-011). "Unavailable" pauses it. Off duty: no timer.
 */
export function evaluateCheckIn(
  d: { onDuty: boolean; status: DutyStatus; lastActivityAt: Date | null; statusSince: Date },
  settings: CheckInSettings,
  now: Date,
): CheckInState {
  if (!d.onDuty || d.status === 'off') return { phase: 'paused', reason: null, dueAt: null, alertAt: null, idleMinutes: 0 };
  const since = d.lastActivityAt && d.lastActivityAt > d.statusSince ? d.lastActivityAt : d.statusSince;
  const idleMinutes = Math.max(0, (now.getTime() - since.getTime()) / 60000);
  if (d.status === 'unavailable') return { phase: 'paused', reason: null, dueAt: null, alertAt: null, idleMinutes };
  const onScene = d.status === 'on_scene';
  const limit = onScene ? settings.onSceneMinutes : settings.inactivityMinutes;
  const grace = settings.answerMinutes;
  const dueAt = new Date(since.getTime() + limit * 60000);
  const alertAt = new Date(dueAt.getTime() + grace * 60000);
  const reason = onScene ? 'on_scene' : 'inactive';
  const phase: CheckInPhase = now >= alertAt ? 'overdue' : now >= dueAt ? 'checkin_due' : 'ok';
  return { phase, reason, dueAt, alertAt, idleMinutes };
}

/** A phone that has moved less than this is treated as stationary. */
export const STATIONARY_METERS = 40;

// ─── Safety alerts on addresses and people (SAFE-001…006) ──────────────────
export const SAFETY_CATEGORIES = ['Aggressive dog', 'Armed / weapons', 'Violent history', 'Mental / behavioral concern', 'Do not serve alone', 'Other hazard'] as const;
export type SafetyCategory = (typeof SAFETY_CATEGORIES)[number];
